import React, { FC, ReactNode, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Colors } from '../../../assets';
import { Spacing } from '../../../assets/GlobalStyle/Variables';
import { PrimaryButton } from '../../elements';
import { FiCheckCircle, FiXCircle, FiInfo, FiX } from 'react-icons/fi';

export type PopUpType = 'success' | 'error' | 'info';

type PopUpProps = {
  message: ReactNode;
  toggle: () => void;
  type: PopUpType;
};

export const PopUp: FC<PopUpProps> = (props) => {
  const { message, toggle, type } = props;

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        toggle();
      }
    },
    [toggle]
  );

  const handleEscKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        toggle();
      }
    },
    [toggle]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [handleEscKey]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <FiCheckCircle />;
      case 'error':
        return <FiXCircle />;
      case 'info':
        return <FiInfo />;
      default:
        return <FiInfo />;
    }
  };

  return (
    <StyledBackdrop onClick={handleBackdropClick}>
      <StyledModalContent type={type}>
        <StyledCloseButton onClick={toggle} aria-label="Close">
          <FiX />
        </StyledCloseButton>

        <StyledIconWrapper type={type}>{getIcon()}</StyledIconWrapper>

        <StyledModalText>{message}</StyledModalText>

        <StyledButtonWrapper>
          <PrimaryButton type="button" onClick={toggle}>
            OK
          </PrimaryButton>
        </StyledButtonWrapper>
      </StyledModalContent>
    </StyledBackdrop>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const StyledBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease-out;
  padding: ${Spacing(2)};

  @media (max-width: 768px) {
    padding: ${Spacing(1)};
  }
`;

type StyledModalContentProps = {
  type: PopUpType;
};

const StyledModalContent = styled.div<StyledModalContentProps>`
  position: relative;
  background-color: ${Colors.white};
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: ${Spacing(5, 4, 4, 4)};
  min-width: 320px;
  max-width: 500px;
  width: 100%;
  animation: ${slideIn} 0.3s ease-out;
  border-top: 4px solid
    ${(props) =>
      props.type === 'success' ? '#28a745' : props.type === 'error' ? Colors.error : Colors.orange};

  @media (max-width: 768px) {
    min-width: 280px;
    padding: ${Spacing(4, 3, 3, 3)};
  }
`;

const StyledCloseButton = styled.button`
  position: absolute;
  top: ${Spacing(2)};
  right: ${Spacing(2)};
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${Colors.grey};
  font-size: 20px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${Colors.lightGrey};
    color: ${Colors.primary};
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

type StyledIconWrapperProps = {
  type: PopUpType;
};

const StyledIconWrapper = styled.div<StyledIconWrapperProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin: 0 auto ${Spacing(3)};
  border-radius: 50%;
  background-color: ${(props) =>
    props.type === 'success' ? '#d4edda' : props.type === 'error' ? '#f8d7da' : '#fff3cd'};

  svg {
    width: 32px;
    height: 32px;
    color: ${(props) =>
      props.type === 'success' ? '#28a745' : props.type === 'error' ? Colors.error : Colors.orange};
  }

  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    margin-bottom: ${Spacing(2)};

    svg {
      width: 28px;
      height: 28px;
    }
  }
`;

const StyledModalText = styled.p`
  text-align: center;
  color: ${Colors.mainText};
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 ${Spacing(4)} 0;
  padding: 0 ${Spacing(2)};
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: ${Spacing(3)};
    padding: 0 ${Spacing(1)};
  }
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: ${Spacing(2)};

  button {
    min-width: 100px;
    padding: ${Spacing(1.5, 4)};
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &:active {
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    button {
      min-width: 80px;
      padding: ${Spacing(1.5, 3)};
    }
  }
`;
